import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth, UserRole } from '../contexts/AuthContext';
import sulibarLogo from '../assets/images/sulibari1.png';

const Login: React.FC = () => {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [isRegistering, setIsRegistering] = useState(false);
  const [selectedRole, setSelectedRole] = useState<UserRole>('student');
  const { login, googleLogin, signup } = useAuth();
  const navigate = useNavigate();

  // Modal state-ek
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);

  const validateForm = () => {
    if (!email || !password) {
      setError(t('validation.emailAndPasswordRequired'));
      return false;
    }
    
    if (isRegistering && password !== confirmPassword) {
      setError(t('validation.passwordsDoNotMatch'));
      return false;
    }
    
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    try {
      setError('');
      setLoading(true);
      
      if (isRegistering) {
        await signup(email, password, selectedRole);
      } else {
        await login(email, password);
      }
      
      navigate('/dashboard');
    } catch (error: any) {
      const errorMessage = error.message || error;
      setError(isRegistering 
        ? `${t('auth.registerFailed')}: ${errorMessage}` 
        : `${t('auth.loginFailed')}: ${errorMessage}`
      );
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    try {
      setError('');
      setLoading(true);
      await googleLogin();
      navigate('/dashboard');
    } catch (error: any) {
      setError(`${t('auth.googleLoginFailed')}: ${error.message || error}`);
    } finally {
      setLoading(false);
    }
  };

  const toggleMode = () => {
    setIsRegistering(!isRegistering);
    setError('');
  };

  // Modal komponensek
  const TermsModal = () => (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-xl w-full max-h-[80vh] flex flex-col">
        <div className="p-5 border-b border-gray-200 flex justify-between items-center">
          <h3 className="text-xl font-semibold text-gray-800">Általános Szerződési Feltételek</h3>
          <button 
            onClick={() => setShowTermsModal(false)}
            className="text-gray-500 hover:text-gray-700"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className="p-5 overflow-y-auto">
          <div className="prose">
            <h4>1. Általános rendelkezések</h4>
            <p>A SuliBari oktatási platform használatával Ön elfogadja jelen Általános Szerződési Feltételeket.</p>
            
            <h4>2. Szolgáltatás leírása</h4>
            <p>A SuliBari platform oktatási tartalmakat, feladatokat és eszközöket biztosít tanárok, szülők és diákok számára az online oktatás elősegítése érdekében.</p>
            
            <h4>3. Regisztráció és felhasználói fiókok</h4>
            <p>A platform használatához regisztráció szükséges. A felhasználó köteles valós adatokat megadni a regisztráció során.</p>
            
            <h4>4. Előfizetések és díjak</h4>
            <p>A platform különböző előfizetési csomagokat kínál, amelyek eltérő funkciókat és lehetőségeket biztosítanak a felhasználók számára.</p>
            
            <h4>5. Felelősség korlátozása</h4>
            <p>A SuliBari nem vállal felelősséget a szolgáltatás használatából eredő esetleges károkért vagy adatvesztésért.</p>
            
            <h4>6. Szerzői jogok</h4>
            <p>A platformon található tartalmak szerzői jogvédelem alatt állnak. A tartalmak engedély nélküli másolása, terjesztése tilos.</p>
            
            <p className="mt-4 italic text-sm text-gray-500">Ez egy minta ÁSZF. A tényleges tartalmat később módosíthatja a platform üzemeltetője.</p>
          </div>
        </div>
        <div className="p-4 border-t border-gray-200 flex justify-end">
          <button 
            onClick={() => setShowTermsModal(false)}
            className="px-4 py-2 bg-[#309D97] text-white rounded-lg hover:bg-opacity-90 transition"
          >
            Bezárás
          </button>
        </div>
      </div>
    </div>
  );

  const PrivacyModal = () => (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-xl w-full max-h-[80vh] flex flex-col">
        <div className="p-5 border-b border-gray-200 flex justify-between items-center">
          <h3 className="text-xl font-semibold text-gray-800">Adatvédelmi Nyilatkozat</h3>
          <button 
            onClick={() => setShowPrivacyModal(false)}
            className="text-gray-500 hover:text-gray-700"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className="p-5 overflow-y-auto">
          <div className="prose">
            <h4>1. Bevezetés</h4>
            <p>A SuliBari platform elkötelezett a felhasználók személyes adatainak védelme mellett.</p>
            
            <h4>2. Adatkezelő</h4>
            <p>Az adatkezelő a SuliBari platform üzemeltetője.</p>
            
            <h4>3. Kezelt adatok köre</h4>
            <p>A platform a következő személyes adatokat kezeli: név, e-mail cím, szerepkör, valamint a platform használatával kapcsolatos adatok.</p>
            
            <h4>4. Adatkezelés célja</h4>
            <p>Az adatkezelés célja a felhasználói fiók létrehozása és kezelése, valamint a szolgáltatás biztosítása.</p>
            
            <h4>5. Adatkezelés időtartama</h4>
            <p>A személyes adatokat a felhasználói fiók megszüntetéséig, vagy az adatkezelési hozzájárulás visszavonásáig kezeljük.</p>
            
            <h4>6. Adatfeldolgozók</h4>
            <p>A platform működéséhez harmadik félként szolgáltatókat (pl. tárhelyszolgáltató) vehetünk igénybe.</p>
            
            <h4>7. Felhasználói jogok</h4>
            <p>A felhasználóknak joguk van a tárolt adataikhoz való hozzáféréshez, azok helyesbítéséhez, törléséhez és hordozhatóságához.</p>
            
            <p className="mt-4 italic text-sm text-gray-500">Ez egy minta adatvédelmi nyilatkozat. A tényleges tartalmat később módosíthatja a platform üzemeltetője.</p>
          </div>
        </div>
        <div className="p-4 border-t border-gray-200 flex justify-end">
          <button 
            onClick={() => setShowPrivacyModal(false)}
            className="px-4 py-2 bg-[#309D97] text-white rounded-lg hover:bg-opacity-90 transition"
          >
            Bezárás
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen flex items-center justify-center relative overflow-hidden">
      {/* Animált, hullámzó háttér */}
      <div className="absolute inset-0 bg-gradient-animated"></div>
      
      {/* Hullámzó elemek */}
      <div className="absolute inset-0 opacity-30">
        <div className="animate-wave1 absolute inset-0 bg-gradient-to-r from-cyan-500 to-teal-300 opacity-20"></div>
        <div className="animate-wave2 absolute inset-0 bg-gradient-to-r from-emerald-500 to-cyan-400 opacity-20"></div>
        <div className="animate-wave3 absolute inset-0 bg-gradient-to-r from-teal-500 to-emerald-300 opacity-20"></div>
      </div>
      
      {showTermsModal && <TermsModal />}
      {showPrivacyModal && <PrivacyModal />}
      
      <div className="bg-white/95 backdrop-blur-sm p-8 rounded-2xl shadow-[0_20px_50px_rgba(0,0,0,0.2)] w-full max-w-md relative z-10 border border-white/20">
        <div className="flex flex-col items-center mb-6">
          <div className="transform hover:scale-105 transition-transform duration-300">
            <img src={sulibarLogo} alt="SuliBari Logo" className="h-28 object-contain drop-shadow-lg" />
          </div>
          <h1 className="font-extrabold text-3xl mt-2 text-[#309D97] tracking-wider" style={{ fontFamily: 'Poppins, sans-serif' }}>
            SuliBari
          </h1>
          <p className="text-gray-500 text-sm mt-1 font-medium">Oktatási Platform</p>
        </div>
        
        <h2 className="text-2xl font-bold text-[#309D97] text-center mb-6">
          {isRegistering ? t('auth.createAccount') : t('auth.welcomeBack')}
        </h2>
        
        {error && (
          <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 rounded-lg mb-4 shadow-sm">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-500" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm">{error}</p>
              </div>
            </div>
          </div>
        )}
        
        <form onSubmit={handleSubmit} className="space-y-5">
          <div className="relative">
            <label className="block text-gray-700 text-sm font-medium mb-1">{t('common.email')}</label>
            <div className="relative">
              <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-500">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207" />
                </svg>
              </span>
              <input
                type="email"
                className="w-full pl-10 pr-3 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#309D97] focus:border-transparent transition-all"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="email@example.com"
                required
              />
            </div>
          </div>
          
          <div className="relative">
            <label className="block text-gray-700 text-sm font-medium mb-1">{t('common.password')}</label>
            <div className="relative">
              <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-500">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
              </span>
              <input
                type="password"
                className="w-full pl-10 pr-3 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#309D97] focus:border-transparent transition-all"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
              />
            </div>
          </div>
          
          {isRegistering && (
            <>
              <div className="relative">
                <label className="block text-gray-700 text-sm font-medium mb-1">{t('common.confirmPassword')}</label>
                <div className="relative">
                  <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-500">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                  </span>
                  <input
                    type="password"
                    className="w-full pl-10 pr-3 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#309D97] focus:border-transparent transition-all"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="••••••••"
                    required
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-gray-700 text-sm font-medium mb-1">{t('common.role')}</label>
                <div className="relative">
                  <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-500">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                  </span>
                  <select
                    className="w-full pl-10 pr-3 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#309D97] focus:border-transparent transition-all"
                    value={selectedRole}
                    onChange={(e) => setSelectedRole(e.target.value as UserRole)}
                    required
                  >
                    <option value="student">{t('roles.student')}</option>
                    <option value="parent">{t('roles.parent')}</option>
                    <option value="teacher">{t('roles.teacher')}</option>
                  </select>
                </div>
                <p className="text-xs text-gray-500 mt-1 italic">
                  {t('auth.adminNoteText')}
                </p>
              </div>
            </>
          )}
          
          <button
            type="submit"
            className="w-full bg-[#309D97] text-white py-3 rounded-lg hover:bg-opacity-90 transition transform hover:-translate-y-1 shadow-lg font-medium text-base"
            disabled={loading}
          >
            {loading ? (
              <div className="flex items-center justify-center">
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                {isRegistering ? t('auth.registerProcess') : t('auth.loginProcess')}
              </div>
            ) : (
              isRegistering ? t('auth.register') : t('auth.login')
            )}
          </button>
        </form>
        
        <div className="mt-6 text-center">
          <button 
            onClick={toggleMode}
            className="text-[#309D97] hover:text-[#1e7d77] text-sm font-medium hover:underline transition-colors"
          >
            {isRegistering 
              ? t('auth.alreadyHaveAccount')
              : t('auth.needAccount')
            }
          </button>
        </div>
        
        <div className="mt-6 relative flex items-center">
          <div className="flex-grow border-t border-gray-300"></div>
          <span className="flex-shrink mx-4 text-gray-500 text-sm">{t('auth.or')}</span>
          <div className="flex-grow border-t border-gray-300"></div>
        </div>
        
        <button
          onClick={handleGoogleLogin}
          className="w-full mt-6 bg-white border border-gray-300 text-gray-700 py-2.5 rounded-lg hover:bg-gray-50 hover:shadow-md transition flex items-center justify-center font-medium"
          disabled={loading}
        >
          <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
            <path
              fill="#4285F4"
              d="M12 5c1.6168 0 3.1013.5978 4.2863 1.5758l3.2837-3.2837C17.8588 1.7758 15.0675 1 12 1 7.0325 1 2.9152 3.8408.7962 8.0042L4.4788 10.84C5.5118 7.1692 8.3752 5 12 5z"
            />
            <path
              fill="#34A853"
              d="M5 12c0-.8525.1397-1.6667.3943-2.4258L.7962 8.0042C.1342 9.1658 0 10.5502 0 12c0 1.4498.1342 2.8342.7962 3.9958l3.598-2.7958C5.1397 13.6667 5 12.8525 5 12z"
            />
            <path
              fill="#FBBC05"
              d="M12 19c-3.6248 0-6.4882-2.1692-7.5212-5.84L.7962 15.9958C2.9152 20.1592 7.0325 23 12 23c2.9938 0 5.7225-.8322 7.7417-2.4065l-3.3948-3.1355C15.4757 18.4468 13.8253 19 12 19z"
            />
            <path
              fill="#EA4335"
              d="M23 12c0-.6168-.1397-1.3343-.3052-2.0198H12v4.2397h6.1198c-.2625 1.4868-1.0763 2.7343-2.2866 3.537l3.3947 3.1355C21.4427 18.5522 23 15.5472 23 12z"
            />
          </svg>
          {t('auth.signInWithGoogle')}
        </button>
        
        {/* ÁSZF és adatvédelmi nyilatkozat linkek */}
        <div className="mt-8 text-center text-xs text-gray-500">
          <p className="mb-2">
            A bejelentkezéssel elfogadod az
            <button 
              onClick={() => setShowTermsModal(true)}
              className="text-[#309D97] hover:underline mx-1 font-medium"
            >
              Általános Szerződési Feltételeket
            </button>
            és az
            <button 
              onClick={() => setShowPrivacyModal(true)}
              className="text-[#309D97] hover:underline mx-1 font-medium"
            >
              Adatvédelmi Nyilatkozatot
            </button>
          </p>
          <p className="mt-2">
            <a href="/help" className="text-[#309D97] hover:underline mx-2">Segítség</a>
            <span className="text-gray-400">|</span>
            <a href="/contact" className="text-[#309D97] hover:underline mx-2">Kapcsolat</a>
          </p>
          <p className="mt-4 text-gray-400">© {new Date().getFullYear()} SuliBari - Minden jog fenntartva</p>
        </div>
      </div>
    </div>
  );
};

export default Login;